import {
  GROUP_RESULT_BAR_HEIGHT,
  HorizontalResultBar,
} from './HorizontalResultBar';
import React, {
  ComponentProps,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import { Box, Tooltip, Typography } from '@mui/material';
import { mergeSx } from '@client/helpers/mui.helpers';
import { makeStyles } from 'tss-react/mui';
import InlineTextField from '@ui/InlineTextField';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { Icon, IconProps } from '@ui/Icon';
import { IconButton } from '@ui/buttons';
import { ISimpleBarChartValue } from '@ui/BarChart';
import MultiLineTooltip from '@ui/MultiLineTooltip';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';
import { AppBarStyles } from '@client/style/style.constants';
import { parseNumericInput } from '@shared/helpers/parse-numeric-input.helpers';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { UNICODE } from '@shared/models/unicode.model';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { KineticNumberDisplay } from '@ui/KineticNumberDisplay';
import {
  useElementListColumnsHovered,
  setElementListColumnsHovered,
  incrementActiveRows,
  decrementActiveRows,
} from './element-list-column-hover.store';
import {
  useElementListColumnResizeHandle,
  ELEMENT_LIST_RESIZE_HANDLE_CLASS,
  ELEMENT_LIST_WIDTH_CSS,
} from './element-list-column-resize.hook';
import { ELEMENT_LIST_HEADER_HEIGHT } from './element-list.constants';
import { shouldSkipSelectedRowAlign } from './element-scroll-request.store';

const ICON_SLOT_SIZE = 12;
const ICON_GAP = 4;
const DEFAULT_ROW_HEIGHT = 32;
const DEFAULT_BAR_COLOR = '#f3ae92';

/**
 * Shared row height used both by the row itself and by any viewport-culling
 * wrapper (e.g. HideOutsideViewport) so off-screen placeholders match the
 * rendered row height and the scroll container's total height stays stable.
 */
export const ELEMENT_LIST_ROW_HEIGHT = DEFAULT_ROW_HEIGHT;

/**
 * Breathing room kept around a row when it is scrolled into view, so a row that
 * was off-screen lands a few rows in from the edge instead of flush against it
 * (`block: 'nearest'` otherwise scrolls the bare minimum).
 */
const SCROLL_MARGIN = DEFAULT_ROW_HEIGHT * 3;

export type ProposalBar = {
  /** Display string (compact-formatted, or en dash for tiny values) shown in the CO₂ column. */
  co2: string;
  /** Display string (compact-formatted, or en dash for tiny values) shown in the cost column. */
  cost: string;
  /** Scientific notation for the CO₂ column tooltip when {@link ProposalBar.co2} is a dash. */
  co2ScientificTooltip?: string;
  /** Scientific notation for the cost column tooltip when {@link ProposalBar.cost} is a dash. */
  costScientificTooltip?: string;
  /**
   * Raw per-GFA co2e value used to compute bar width against `maxCo2`. Kept
   * separate from {@link ProposalBar.co2} since `parseFloat` of a compact
   * string like "1.2K" silently strips the unit suffix and produces wildly
   * wrong percentages.
   */
  co2Value: number;
  /**
   * Raw cost value (in display currency) used to compute bar width against
   * `maxCost`. See {@link ProposalBar.co2Value} for rationale.
   */
  costValue: number;
  /**
   * Optional baseline (pre-proposal) per-GFA co2e value. When set, renders a
   * grey baseline bar behind the proposal bar so the magnitude of the
   * proposal's change is visible against the original.
   */
  baselineCo2Value?: number;
  color?: string;
  co2Segments?: ISimpleBarChartValue[];
};

export type ElementListRowVariant = 'element' | 'group';

/** Position of a selected row within a run of adjacent selected rows. */
export type SelectedBlockPosition = 'single' | 'first' | 'middle' | 'last';

const SMALL_TEXT_STYLE = { fontSize: 11, letterSpacing: '0.55px' } as const;

const valueFieldDisplayBaseSx = {
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'center',
  height: 24,
  borderRadius: '2px',
  cursor: 'pointer',
  px: '2px',
  boxSizing: 'border-box',
} as const;

const nameRowBaseSx = {
  display: 'flex',
  flex: 1,
  minWidth: 0,
  alignItems: 'center',
} as const;

const pointerCursorSx = { cursor: 'pointer' } as const;

const mappingColumnsSx = {
  display: 'flex',
  alignItems: 'stretch',
  flexShrink: 0,
} as const;

const barsColumnSx = {
  flex: 1,
  alignSelf: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
} as const;

const metricsRowSx = {
  display: 'flex',
  alignSelf: 'stretch',
  flexShrink: 0,
} as const;

const dimOpacitySx = { opacity: 0.5 } as const;

const formatCompactKpi = getDisplayNumber.withOptions({ notation: 'compact' });

/**
 * Renders a proposal CO₂/cost KPI value: an animated compact number for real
 * non-zero values, otherwise the pre-formatted {@link text} verbatim. Static
 * text covers the scientific-tooltip case (tiny values shown as an en dash) and
 * the absent-value case, where {@link text} already carries the correct fallback
 * (blank for missing cost, `"0"` for missing CO₂) — so animating a `0` value
 * never overwrites it.
 */
const CompactKpiValue: React.FC<{
  /** Pre-formatted display string used whenever {@link value} isn't animated. */
  text: string;
  /** Raw numeric value driving the animated transition. */
  value: number;
  /** Scientific notation for tiny magnitudes; when set, {@link text} is shown as-is. */
  scientificTooltip: string | undefined;
}> = ({ text, value, scientificTooltip }) =>
  scientificTooltip || value === 0 ? (
    <>{text}</>
  ) : (
    <KineticNumberDisplay value={value} format={formatCompactKpi} />
  );

export const UnitSelector: React.FC<{
  unit: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  color?: string;
  fontWeight?: number | string;
  fontSize?: number;
}> = ({ unit, onClick, color = 'rgba(0,0,0,0.3)', fontWeight, fontSize }) => (
  <Box
    component={onClick ? 'button' : 'div'}
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      height: 24,
      background: 'none',
      border: 'none',
      px: '2px',
      boxSizing: 'border-box',
      borderRadius: '2px',
      cursor: onClick ? 'pointer' : 'default',

      ...(onClick && {
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
      }),
    }}
  >
    <Typography
      noWrap
      sx={{
        ...SMALL_TEXT_STYLE,
        color,
        fontWeight,
        ...(fontSize != null && { fontSize }),
      }}
    >
      {unit}
    </Typography>
  </Box>
);

export const SBEF_PREFIX_SX = {
  ...SMALL_TEXT_STYLE,
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  color: 'rgba(0,0,0,0.5)',
  position: 'absolute' as const,
  right: 0,
  whiteSpace: 'nowrap' as const,
} as const;

export type ElementListRowHandle = {
  startRename(): void;
};

export type ElementListRowProps = {
  depth?: number;
  height?: number;
  name?: string;
  namePlaceholder?: string;
  quantityValue?: string;
  quantityDisplayValue?: string;
  /** Quantity after display-unit conversion, used for animated transitions. */
  quantityDisplayNumber?: number;
  quantityPlaceholder?: string;
  /** Editable number the empty quantity field fills on ArrowRight. */
  quantityFillValue?: string;
  unit?: string;
  /**
   * Cosmetic unit shown next to {@link quantityDisplayValue} when not editing.
   * Lets the connected row auto-format (e.g. mm → m) without altering the
   * underlying {@link unit}, which is still used in edit mode so input always
   * happens in the value's actual unit.
   */
  quantityDisplayUnit?: string;
  proposals?: ProposalBar[];
  maxCo2?: number;
  maxCost?: number;
  variant?: ElementListRowVariant;
  groupNumber?: number | string;
  expandable?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  selected?: boolean;
  /**
   * Where a selected row sits in a run of adjacent selected rows (DEV-3456).
   * Only the outer edges of a run draw a border, so a multi-select block reads
   * as one shape. Defaults to `'single'`, i.e. the classic single-select look.
   */
  selectedBlockPosition?: SelectedBlockPosition;
  onSelect?: (e: React.MouseEvent<HTMLElement>) => void;
  scrollTo?: boolean;
  categoryIcon?: IconProps<'elementCategory'>['icon'];
  /**
   * When provided in variant="group", renders this icon in the left slot
   * instead of the groupNumber text.
   */
  groupIcon?: IconProps<'elementCategory'>['icon'];
  /**
   * When provided in variant="group", renders in the left expand/chevron slot
   * while the row is not hovered (replaced by the expand chevron on hover).
   */
  groupLeadingSlot?: React.ReactNode;
  /**
   * When provided, replaces the category icon in the left slot with arbitrary
   * content. Takes precedence over {@link categoryIcon}. Pass `null` to omit
   * the main icon (non-`group` rows only).
   */
  iconSlot?: React.ReactNode | null;
  /**
   * When true, omits the main type icon on non-`group` rows without using
   * {@link iconSlot} (use for rows that should start flush with {@link leftGap} only).
   * Ignored if {@link iconSlot} is a node (custom slot still renders).
   */
  hideLeadingTypeIcon?: boolean;
  /**
   * Extra icon-sized slots rendered as flex siblings to the LEFT of the main
   * icon container, each in a 12×12 position:relative box separated by ICON_GAP.
   * Useful for ancestor icon breadcrumbs (e.g. ActionElementRow).
   */
  prefixIconSlots?: React.ReactNode[];
  /**
   * When false, disables the resizable left column: removes the drag handle
   * and uses full available width instead of the shared CSS variable.
   * Defaults to true. Set to false when using this row outside the element list.
   */
  resizable?: boolean;
  onNameSave?: (value: string) => void;
  /**
   * Optional click handler on the name slot. When set, the name area becomes
   * a single-click target (in addition to whatever the rename input would do
   * via {@link nameReadonly} / `triggerOn="doubleClick"`). Used by csv-import
   * to anchor the in-row product picker popover to the name cell.
   */
  onNameClick?: React.MouseEventHandler<HTMLElement>;
  /** Clicks on the resizable left column (icon, name, qty). Row select is not triggered when set. */
  onLeftColumnClick?: React.MouseEventHandler<HTMLElement>;
  /**
   * When provided, replaces the inline name input/display with custom content
   * (e.g. a typeahead product picker). The override sits inside the same flex
   * cell so the rest of the row layout (icon, qty, unit, trailing) is unchanged.
   */
  nameOverrideSlot?: React.ReactNode;
  /** Content between the type/lock icon and the name. */
  nameLeadingSlot?: React.ReactNode;
  onQtySave?: (value: number | undefined) => void;
  onKebabClick?: React.MouseEventHandler<HTMLElement>;
  /**
   * Right-click handler. Defaults to {@link onKebabClick} so a kebab that opens
   * a menu also opens on context-menu. Pass explicitly to decouple right-click
   * from a kebab that performs a direct action (e.g. navigate / import), so a
   * right-click never fires that action.
   */
  onContextMenu?: React.MouseEventHandler<HTMLElement>;
  kebabIcon?: keyof typeof import('@ui/Icon').muiIcons;
  kebabTooltip?: string;
  kebabMenuOpen?: boolean;
  onUnitClick?: React.MouseEventHandler<HTMLElement>;
  onQtyFxClick?: () => void;
  onFocusClick?: () => void;
  disabled?: boolean;
  showDisabledIcon?: boolean;
  quantityReadonly?: boolean;
  /** Description of the quantity and its unit, shown on hover. */
  quantityTooltip?: string;
  quantityFontWeight?: number;
  quantityFontSize?: number;
  quantityColor?: string;
  /**
   * When true, omits the quantity value field and unit column (used for SBEF group header rows).
   */
  hideQuantity?: boolean;
  nameReadonly?: boolean;
  hideDivider?: boolean;
  /** Overrides the display (non-editing) style of the name text field. */
  nameDisplayStyle?: React.CSSProperties;
  /**
   * When true, replaces the warm surface background with transparent on the row,
   * resizable container, and kebab button. The hover overlay (::after) is preserved.
   */
  hideSurfaceBackground?: boolean;
  /**
   * Width in px of the left spacer gap inside the resizable container.
   * Defaults to 12.
   */
  leftGap?: number;
  /**
   * When true, dims (opacity 0.5) and blocks pointer interaction on the name
   * field and quantity/unit columns. Does not affect the row background,
   * selection ring, or the kebab/status cluster — use this for discarded rows
   * where the row itself remains selectable but its content should be inactive.
   */
  contentDisabled?: boolean;
  /**
   * When true, omits the category icon and the quantity/unit columns.
   * The name field and {@link kebabLeadingSlot} are unchanged.
   */
  hideMappingColumns?: boolean;
  /**
   * Renders immediately to the right of the name field (ValueField or
   * nameOverrideSlot), before the quantity column. Use for inline row actions
   * like a navigation arrow.
   * A function can receive `isRowHovered` (e.g. to match kebab visibility).
   */
  nameTrailingSlot?:
    React.ReactNode | ((isRowHovered: boolean) => React.ReactNode);
  /**
   * Renders to the left of the kebab (e.g. cost delta). The name field reserves
   * extra right padding when the kebab is shown and quantity is hidden.
   * A function can receive `isRowHovered` (e.g. to hide a number but keep width).
   */
  kebabLeadingSlot?:
    React.ReactNode | ((isRowHovered: boolean) => React.ReactNode);
  /**
   * When true, {@link kebabLeadingSlot} receives pointer events (tooltips, clicks).
   * Default false so display-only KPI clusters pass row clicks through.
   */
  kebabLeadingInteractive?: boolean;
  /**
   * Optional horizontal nudge of the leading KPI block before the kebab.
   * Prefer `0` — positive values shift the cluster right while name `paddingRight`
   * is unchanged, which creates a dead gap before the first KPI. Default 0.
   */
  kpiClusterTranslateX?: number;
  /**
   * Extra right padding on the name field when `hideQuantity` and
   * `kebabLeadingSlot` are set. Default `98px` (action KPI row, two columns);
   * pass a smaller value when one column is shown.
   */
  kebabLeadingNamePaddingRight?: string;
  /**
   * Right padding on the name field when `hideQuantity`, the kebab is shown, and
   * there is no `kebabLeadingSlot`. Default `28px` (reserves kebab hit area);
   * pass `0` when `innerTrailingSlot` already clears the kebab (e.g. import rows).
   */
  hideQuantityMenuNamePaddingRight?: string;
  /**
   * When set (with {@link resizable}), replaces proposal bars and per-m² KPI
   * columns with arbitrary trailing content — e.g. import status + CSV columns.
   */
  trailingSlot?: React.ReactNode;
  /** Left inset in pixels for custom trailing content. Defaults to 8. */
  trailingSlotPaddingLeft?: number;
  /**
   * Renders inside the resizable left container, flush to the right edge just
   * before the resize handle. Use for fixed-width content that should stay in
   * the left column (e.g. the import status cell).
   */
  innerTrailingSlot?: React.ReactNode;
};

const getValueFieldColor = ({
  value,
  placeholder,
  type,
  hovered,
  showHover,
}: {
  value: string;
  placeholder: string | undefined;
  type: 'text' | 'number';
  hovered: boolean;
  showHover: boolean;
}): string => {
  const isHoverActive = hovered && showHover;

  if (!value && placeholder) {
    return isHoverActive ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)';
  }
  if (type === 'number' && !value) {
    return isHoverActive ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)';
  }
  if (type === 'number' && value) {
    return 'rgba(0,0,0,0.8)';
  }
  return isHoverActive ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)';
};

const getValueFieldDisplayText = ({
  value,
  displayValue,
  placeholder,
  type,
}: {
  value: string;
  displayValue: string | undefined;
  placeholder: string | undefined;
  type: 'text' | 'number';
}): string => {
  if (!value && placeholder) return placeholder;
  if (type === 'number' && !value)
    return `${UNICODE.SPACES.NON_BREAKING}${UNICODE.SPACES.NON_BREAKING}${UNICODE.DASH.EN_DASH}${UNICODE.SPACES.NON_BREAKING}${UNICODE.SPACES.NON_BREAKING}`;
  return displayValue ?? value;
};

/**
 * Marks an inline editing surface inside a row. A row binds `onClick` on its
 * whole box, so list selection needs to tell "clicked the row" apart from
 * "clicked into an editor" — and the click that *opens* an editor lands on the
 * display box, before any input exists to detect (DEV-3456).
 */
export const INLINE_EDITOR_ATTRIBUTE = 'data-inline-editor';

export const ValueField: React.FC<{
  value: string;
  displayValue?: string;
  onSave: (v: string) => void;
  onStartEditing: (e?: React.MouseEvent) => void;
  onStopEditing: () => void;
  isEditing: boolean;
  textAlign?: 'left' | 'right';
  fullWidth?: boolean;
  /** Display mode only: size to text (with ellipsis) instead of filling the row. */
  contentSized?: boolean;
  triggerOn?: 'click' | 'doubleClick';
  displayStyle?: React.CSSProperties;
  editingStyle?: React.CSSProperties;
  type?: 'text' | 'number';
  showHover?: boolean;
  placeholder?: string;
  /** Editable text the empty field fills on ArrowRight, when the grayed
   *  placeholder is a formatted display value that can't be edited directly. */
  fillValue?: string;
  onFxClick?: () => void;
  onDoubleClickOverride?: () => void;
  /** Fires on every keystroke while editing. Used by typeahead wrappers. */
  onChange?: (v: string) => void;
  /** Raw display-space number used instead of a pre-formatted numeric string. */
  animatedValue?: number;
  /** Remounts the animation when its display domain, such as the unit, changes. */
  animationKey?: React.Key;
  /** Forwarded to {@link InlineTextField.onKeyDown}. Used by typeahead wrappers
   *  to handle Up/Down/Enter while the input has focus. */
  onKeyDown?: (e: React.KeyboardEvent) => void;
}> = ({
  value,
  displayValue,
  onSave,
  onStartEditing,
  onStopEditing,
  isEditing,
  textAlign = 'right',
  fullWidth = false,
  contentSized = false,
  triggerOn = 'click',
  displayStyle,
  editingStyle,
  type = 'text',
  showHover = true,
  placeholder,
  fillValue,
  onFxClick,
  onDoubleClickOverride,
  onChange,
  onKeyDown,
  animatedValue,
  animationKey,
}) => {
  const [hovered, setHovered] = useState(false);

  if (isEditing) {
    return (
      <Box
        {...{ [INLINE_EDITOR_ATTRIBUTE]: '' }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'center',
          gap: '4px',
          height: 24,
          flex: fullWidth ? 1 : undefined,
          minWidth: fullWidth ? 0 : undefined,
          flexShrink: fullWidth ? undefined : 0,
          borderRadius: '2px',
          outline: '1px solid',
          outlineColor: 'custom.focus',
          backgroundColor: 'white',
          px: '2px',
          boxSizing: 'border-box',
        }}
      >
        {onFxClick && (
          <IconButton
            category="ui"
            icon="fx"
            size={ICON_SLOT_SIZE}
            disableRipple
            sx={{
              p: 0,
              flexShrink: 0,
              ml: '2px',
            }}
            // Focus lands here on click; the input's blur handler sees this flag and
            // keeps the edit alive (see InlineTextField) so this button survives to
            // run its own onClick instead of being torn down first.
            data-keep-inline-edit
            onClick={(e) => {
              e.stopPropagation();
              onFxClick();
              // Leave inline edit so the still-focused input can't commit its own
              // (empty) value on blur and clobber the expression the panel saves.
              onStopEditing();
            }}
          />
        )}
        <Box
          sx={{
            height: AppBarStyles.HEIGHT,
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
          }}
        >
          <InlineTextField
            value={value}
            editing
            defaultValue={placeholder}
            fillValue={fillValue}
            onSave={(v) => {
              onSave(v);
              onStopEditing();
            }}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onCancel={onStopEditing}
            fullWidth
            textFieldProps={{
              sx: {
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiOutlinedInput-root': { padding: 0 },
                '& input': { textAlign },
                '& input::placeholder': {
                  color: 'rgba(0,0,0,0.2)',
                },
              },
              InputProps: {
                inputProps: {
                  onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                    e.target.select(),
                  // No per-keystroke character filter: input is validated and
                  // normalised at the save boundary (parseNumericInput), so any
                  // source — numpad comma, paste, IME, named-function formulas
                  // like sqrt(4) — is handled uniformly. Only Up/Down stepping
                  // needs to intercept the key here.
                  onKeyDown:
                    type === 'number'
                      ? (e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')
                            return;
                          e.preventDefault();
                          const step = e.shiftKey ? 10 : 1;
                          const delta = e.key === 'ArrowUp' ? step : -step;
                          const current =
                            parseNumericInput(e.currentTarget.value) ?? 0;
                          const next = String(current + delta);
                          const nativeSetter = Object.getOwnPropertyDescriptor(
                            window.HTMLInputElement.prototype,
                            'value',
                          )?.set;
                          nativeSetter?.call(e.currentTarget, next);
                          e.currentTarget.dispatchEvent(
                            new Event('input', { bubbles: true }),
                          );
                        }
                      : undefined,
                  style: {
                    ...SMALL_TEXT_STYLE,
                    textAlign,
                    padding: 0,
                    paddingTop: '1px',
                    color: 'rgba(0,0,0,0.8)',
                    WebkitTextFillColor: 'rgba(0,0,0,0.8)',
                    ...editingStyle,
                  },
                },
              },
            }}
          />
        </Box>
      </Box>
    );
  }

  const displayFlexProps = contentSized
    ? { flex: '0 1 auto' as const, minWidth: 0 }
    : fullWidth
      ? { flex: 1 as const, minWidth: 0 }
      : { flexShrink: 0 as const };
  const displayOverflow = contentSized || fullWidth ? 'hidden' : undefined;

  return (
    <Box
      {...{ [INLINE_EDITOR_ATTRIBUTE]: '' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={
        triggerOn === 'click'
          ? (e) => {
              setHovered(false);
              onStartEditing(e);
            }
          : undefined
      }
      onDoubleClick={
        onDoubleClickOverride
          ? () => {
              setHovered(false);
              onDoubleClickOverride();
            }
          : triggerOn === 'doubleClick'
            ? (e) => {
                setHovered(false);
                onStartEditing(e);
              }
            : undefined
      }
      sx={mergeSx(displayFlexProps, valueFieldDisplayBaseSx, {
        justifyContent: textAlign === 'right' ? 'flex-end' : 'flex-start',
        overflow: displayOverflow,
        backgroundColor:
          hovered && showHover ? 'rgba(0,0,0,0.05)' : 'transparent',
      })}
    >
      <Typography
        noWrap
        sx={{
          ...SMALL_TEXT_STYLE,
          ...(fullWidth && {
            minWidth: 0,
            width: contentSized ? 'fit-content' : '100%',
            textAlign,
          }),
          color: getValueFieldColor({
            value,
            placeholder,
            type,
            hovered,
            showHover,
          }),
          fontWeight: type === 'number' && value ? 500 : undefined,
          ...displayStyle,
        }}
      >
        {type === 'number' &&
        animatedValue !== undefined &&
        Number.isFinite(animatedValue) ? (
          <KineticNumberDisplay
            key={animationKey}
            value={animatedValue}
            format={getDisplayNumber}
          />
        ) : (
          getValueFieldDisplayText({ value, displayValue, placeholder, type })
        )}
      </Typography>
    </Box>
  );
};

type NumberValueFieldProps = Omit<
  ComponentProps<typeof ValueField>,
  'onSave' | 'type'
> & {
  /**
   * Receives the parsed value: a finite number, or `undefined` when the field
   * was cleared. Unparsable input (e.g. a malformed formula) never fires this —
   * the edit is discarded and the field resets to its previous value.
   */
  onSave: (value: number | undefined) => void;
};

/**
 * Numeric variant of {@link ValueField}: the single place that turns inline
 * quantity-style input into a typed number via {@link parseNumericInput}
 * (formatted numbers first, then Math.js formulas). Consumers get a
 * `number | undefined` instead of a raw string, so no per-call-site parsing.
 */
export const NumberValueField: React.FC<NumberValueFieldProps> = ({
  onSave,
  ...rest
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const handleSave = useCallback(
    (raw: string) => {
      if (raw.trim() === '') {
        onSave(undefined);
        return;
      }
      const value = parseNumericInput(raw);
      if (value === undefined) {
        enqueueSnackbar(
          "We couldn't read that as a number. Try a value like 12, 1.5 or 2 * 3.",
          { variant: 'warning' },
        );
        return;
      }
      onSave(value);
    },
    [onSave, enqueueSnackbar],
  );

  return <ValueField {...rest} type="number" onSave={handleSave} />;
};

const IndentChevron: React.FC = () => (
  <Box
    sx={{
      width: ICON_SLOT_SIZE,
      height: ICON_SLOT_SIZE,
      flexShrink: 0,
    }}
  />
);

/** Shared CO₂ and cost columns for Build and project list rows. */
export const ElementListResultColumns = ({
  proposals,
  visible,
  dimmed = false,
  reserveCostSpace = false,
}: {
  proposals: ProposalBar[];
  visible: boolean;
  dimmed?: boolean;
  reserveCostSpace?: boolean;
}) => {
  const displayCurrency = useDisplayCurrency();
  const co2eVisible = useCo2eVisible();
  return (
    <Box
      onMouseEnter={() => setElementListColumnsHovered(true)}
      onMouseLeave={() => setElementListColumnsHovered(false)}
      sx={mergeSx(metricsRowSx, dimmed ? dimOpacitySx : undefined)}
    >
      {co2eVisible.isEnabled && (
        <Box
          sx={{
            width: 60,
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',

            visibility: visible ? 'visible' : 'hidden',
          }}
        >
          <MultiLineTooltip
            disableInteractive
            rows={
              proposals[0]?.co2ScientificTooltip
                ? ['Carbon kgCO2e per m²', proposals[0].co2ScientificTooltip]
                : ['Carbon kgCO2e per m²']
            }
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                ...SMALL_TEXT_STYLE,
                color: 'rgba(0,0,0,0.5)',
                fontWeight: 700,
              }}
            >
              {proposals[0] && (
                <CompactKpiValue
                  text={proposals[0].co2}
                  value={proposals[0].co2Value}
                  scientificTooltip={proposals[0].co2ScientificTooltip}
                />
              )}
            </Typography>
          </MultiLineTooltip>
        </Box>
      )}

      <Box
        sx={{
          width: 60,
          alignSelf: 'stretch',
          alignItems: 'center',
          justifyContent: 'center',
          visibility: visible ? 'visible' : 'hidden',
          display:
            displayCurrency.isDisabled && !reserveCostSpace ? 'none' : 'flex',
        }}
      >
        <MultiLineTooltip
          disableInteractive
          rows={
            proposals[0]?.costScientificTooltip
              ? [
                  `Expenses ${displayCurrency.current} per m²`,
                  proposals[0].costScientificTooltip,
                ]
              : [`Expenses ${displayCurrency.current} per m²`]
          }
        >
          <Typography
            variant="body2"
            noWrap
            sx={{ ...SMALL_TEXT_STYLE, color: 'rgba(0,0,0,0.5)' }}
          >
            {displayCurrency.isEnabled && proposals[0] && (
              <CompactKpiValue
                text={proposals[0].cost}
                value={proposals[0].costValue}
                scientificTooltip={proposals[0].costScientificTooltip}
              />
            )}
          </Typography>
        </MultiLineTooltip>
      </Box>
    </Box>
  );
};

const ElementListRow = React.forwardRef<
  ElementListRowHandle,
  ElementListRowProps
>(
  (
    {
      depth = 1,
      height = DEFAULT_ROW_HEIGHT,
      name: initialName = '',
      namePlaceholder,
      quantityValue: initialQty = '',
      quantityDisplayValue,
      quantityDisplayNumber,
      quantityPlaceholder,
      quantityFillValue,
      unit = '',
      quantityDisplayUnit,
      proposals = [],
      maxCo2 = 300,
      maxCost = 300,
      variant = 'element',
      groupNumber,
      groupIcon,
      groupLeadingSlot,
      expandable = false,
      expanded = false,
      onExpandedChange,
      selected = false,
      selectedBlockPosition = 'single',
      onSelect,
      scrollTo = false,
      categoryIcon = 'slab',
      iconSlot,
      hideLeadingTypeIcon = false,
      prefixIconSlots = [],
      resizable = true,
      onNameSave,
      onNameClick,
      onLeftColumnClick,
      nameOverrideSlot,
      nameLeadingSlot,
      onQtySave,
      onKebabClick,
      onContextMenu,
      kebabIcon = 'MoreVert',
      kebabTooltip,
      kebabMenuOpen = false,
      onUnitClick,
      onQtyFxClick,
      onFocusClick,
      disabled = false,
      showDisabledIcon = false,
      quantityReadonly = false,
      quantityTooltip,
      quantityFontWeight,
      quantityFontSize,
      quantityColor,
      hideQuantity = false,
      nameReadonly = false,
      hideDivider = false,
      nameDisplayStyle,
      hideSurfaceBackground = false,
      leftGap = 12,
      contentDisabled = false,
      hideMappingColumns = false,
      nameTrailingSlot,
      kebabLeadingSlot,
      kebabLeadingInteractive = false,
      kpiClusterTranslateX = 0,
      kebabLeadingNamePaddingRight = '98px',
      hideQuantityMenuNamePaddingRight = '28px',
      trailingSlot,
      trailingSlotPaddingLeft = 8,
      innerTrailingSlot,
    },
    ref,
  ) => {
    const [rowHovered, setRowHovered] = useState(false);
    const columnsHovered = useElementListColumnsHovered();
    const displayCurrency = useDisplayCurrency();
    const co2eVisible = useCo2eVisible();

    const isRowActive = rowHovered || selected;
    useEffect(() => {
      if (isRowActive) {
        incrementActiveRows();
        return decrementActiveRows;
      }
    }, [isRowActive]);
    const handleDragStart = useElementListColumnResizeHandle();
    const rowRef = useRef<HTMLDivElement>(null);
    const prevScrollTo = usePrevious(scrollTo);
    useEffect(() => {
      // `!== true` (not `=== false`) so a row that mounts already selected —
      // e.g. opening a URL that points at an element deep in the list — also
      // scrolls. On mount `prevScrollTo` is `undefined`, so there is never a
      // false → true transition to catch.
      const row = rowRef.current;
      if (!scrollTo || prevScrollTo === true || !row) return;
      // Tab restore already set `main`; remounted selected rows must not jump it.
      if (shouldSkipSelectedRowAlign()) return;

      // Measure against the scroll container rather than the window: it starts
      // below the app bar, and its top is further covered by the sticky list
      // header. A row hidden behind either is still within the window, so a
      // window-based check would call it visible and skip the scroll.
      const scrollRoot = row.closest('main');
      const rowRect = row.getBoundingClientRect();
      const topEdge =
        (scrollRoot?.getBoundingClientRect().top ?? 0) +
        ELEMENT_LIST_HEADER_HEIGHT;
      const bottomEdge =
        scrollRoot?.getBoundingClientRect().bottom ?? window.innerHeight;

      if (rowRect.top < topEdge || rowRect.bottom > bottomEdge) {
        row.scrollIntoView({ behavior: 'auto', block: 'nearest' });
      }
    }, [scrollTo, prevScrollTo]);

    const [isEditingName, startEditingName, stopEditingName] =
      useBooleanState(false);

    useImperativeHandle(ref, () => ({ startRename: startEditingName }), [
      startEditingName,
    ]);

    const [isEditingQty, startEditingQty, stopEditingQty] =
      useBooleanState(false);
    const [nameValue, setNameValue] = useState(initialName);
    const [qtyValue, setQtyValue] = useState(initialQty);
    useEffect(() => {
      if (!isEditingName) setNameValue(initialName);
    }, [initialName, isEditingName]);
    useEffect(() => {
      if (!isEditingQty) setQtyValue(initialQty);
    }, [initialQty, isEditingQty]);
    // Unified disabled treatment: deactivated (`disabled`) and discarded/skipped
    // (`contentDisabled`) rows both dim their CONTENT cells (name, quantity,
    // proposal bars, KPI columns) while the kebab/status control cluster stays
    // bright. `contentDisabled` additionally locks pointer interaction on the
    // editable cells so a discarded row can't be edited until restored.
    const dimContent = disabled || contentDisabled;
    const lockContent = contentDisabled;
    const contentDimSx = dimContent
      ? ({
          opacity: 0.5,
          ...(lockContent ? { pointerEvents: 'none' } : undefined),
        } as const)
      : undefined;

    // Kebab / disabled-eye button: only visible on hover, when its menu is open,
    // or when it's the persistent disabled indicator — and dimmed along with the
    // rest of the content on disabled rows. It blends into the row surface, with
    // a hover tint that doesn't fight the selection/transparent backgrounds.
    const kebabVisible = showDisabledIcon || rowHovered || kebabMenuOpen;

    // Borders stay in the box model and only change colour, so a row keeps the
    // same content height whichever part of a selected block it is.
    const selectedBorder = (visible: boolean) =>
      selected
        ? `1px solid ${visible ? 'var(--element-list-selected-border-color, #0B99FF)' : 'transparent'}`
        : undefined;

    const { classes } = useStyles({
      selected,
      resizable,
      hideSurfaceBackground,
      kebabVisible,
      dimContent,
      kebabClickable: Boolean(onKebabClick),
    });

    const handleNameSave = useCallback(
      (value: string) => {
        setNameValue(value);
        stopEditingName();
        onNameSave?.(value);
      },
      [stopEditingName, onNameSave],
    );

    const handleQtySave = useCallback(
      (value: number | undefined) => {
        setQtyValue(value !== undefined ? String(value) : '');
        stopEditingQty();
        onQtySave?.(value);
      },
      [stopEditingQty, onQtySave],
    );
    const shouldRenderMenuAction =
      Boolean(onKebabClick) || showDisabledIcon || kebabLeadingSlot != null;
    const omitMainTypeIcon =
      iconSlot === null || (hideLeadingTypeIcon && iconSlot === undefined);

    const kpiSlotContent = useMemo(
      () =>
        kebabLeadingSlot == null
          ? null
          : typeof kebabLeadingSlot === 'function'
            ? kebabLeadingSlot(rowHovered)
            : kebabLeadingSlot,
      [kebabLeadingSlot, rowHovered],
    );

    const nameTrailingContent = useMemo(
      () =>
        nameTrailingSlot == null
          ? null
          : typeof nameTrailingSlot === 'function'
            ? nameTrailingSlot(rowHovered)
            : nameTrailingSlot,
      [nameTrailingSlot, rowHovered],
    );

    return (
      <>
        {variant === 'group' && !hideDivider && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 5,
              width: '100%',
              backgroundColor: 'warmSurface.main',
            }}
          >
            <Box
              className={classes.resizableContainerDivider}
              sx={{
                position: 'relative',
                flexShrink: resizable ? 0 : 1,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                width: resizable ? ELEMENT_LIST_WIDTH_CSS : '100%',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                }}
              />
              {resizable && (
                <Box
                  className={ELEMENT_LIST_RESIZE_HANDLE_CLASS}
                  onMouseDown={handleDragStart}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    cursor: 'ew-resize',
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
              }}
            />
            {trailingSlot == null && (
              <Box
                onMouseEnter={() => setElementListColumnsHovered(true)}
                onMouseLeave={() => setElementListColumnsHovered(false)}
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  height: '100%',
                }}
              >
                {co2eVisible.isEnabled && (
                  <Box
                    sx={{
                      width: 60,
                    }}
                  />
                )}
                {!displayCurrency.isDisabled && (
                  <Box
                    sx={{
                      width: 60,
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
        <Box
          ref={rowRef}
          className="row-hover"
          role="treeitem"
          aria-level={Math.max(1, depth)}
          aria-selected={selected || undefined}
          aria-expanded={expandable ? expanded : undefined}
          aria-label={nameValue || namePlaceholder}
          onMouseEnter={() => setRowHovered(true)}
          onMouseLeave={() => setRowHovered(false)}
          onClick={onSelect}
          onContextMenu={onContextMenu ?? onKebabClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: height,
            width: '100%',
            boxSizing: 'border-box',
            userSelect: 'none',
            position: 'relative',

            backgroundColor: selected
              ? 'var(--element-list-selected-background, #E2F2FF)'
              : hideSurfaceBackground
                ? 'transparent'
                : 'warmSurface.main',

            scrollMarginTop: `${SCROLL_MARGIN}px`,
            scrollMarginBottom: `${SCROLL_MARGIN}px`,

            borderTop: selectedBorder(
              selectedBlockPosition === 'single' ||
                selectedBlockPosition === 'first',
            ),

            borderBottom: selectedBorder(
              selectedBlockPosition === 'single' ||
                selectedBlockPosition === 'last',
            ),

            // One full-row hover wash above opaque left-column fills (name /
            // MappingPicker / kebab) so left and right share the same grey.
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              backgroundColor:
                kebabMenuOpen && !selected ? 'rgba(0,0,0,0.05)' : 'transparent',
              pointerEvents: 'none',
            },

            '&:hover::after': {
              backgroundColor: !selected ? 'rgba(0,0,0,0.05)' : 'transparent',
            },
          }}
        >
          {/* Resizable left container */}
          <Box
            className={classes.resizableContainer}
            onClick={onLeftColumnClick}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'stretch',
              flexShrink: resizable ? 0 : 1,
              height: '100%',
              width: resizable ? ELEMENT_LIST_WIDTH_CSS : '100%',
              overflow: 'hidden',
              ...(onLeftColumnClick ? { cursor: 'pointer' } : undefined),
            }}
          >
            <Box
              sx={{
                width: leftGap,
                flexShrink: 0,
              }}
            />

            {/* depth × chevron indicators + element type icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'stretch',
                flexShrink: 0,
                gap: `${ICON_GAP}px`,
              }}
            >
              {Array.from({ length: depth - 1 }).map((_, i) => {
                const isLast = i === depth - 2;
                return isLast && expandable && rowHovered ? (
                  <Box
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpandedChange?.(!expanded);
                    }}
                    sx={{
                      position: 'relative',
                      width: ICON_SLOT_SIZE,
                      height: ICON_SLOT_SIZE,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      opacity: 0.5,
                      cursor: 'pointer',
                      transform: expanded ? 'rotate(90deg)' : 'none',
                      transition: 'opacity 0.15s ease, transform 0.15s ease',
                      zIndex: 1,

                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: -4,
                      },

                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Icon
                      category="mui"
                      icon="ArrowRight"
                      size={ICON_SLOT_SIZE}
                    />
                  </Box>
                ) : (
                  <IndentChevron key={i} />
                );
              })}
              {prefixIconSlots.map((slot, i) => (
                <Box
                  key={i}
                  sx={{
                    position: 'relative',
                    width: ICON_SLOT_SIZE,
                    height: ICON_SLOT_SIZE,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    opacity: 0.4,
                  }}
                >
                  {slot}
                </Box>
              ))}
              {variant === 'group' &&
              expandable &&
              rowHovered &&
              depth === 1 ? (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpandedChange?.(!expanded);
                  }}
                  sx={{
                    position: 'relative',
                    width: ICON_SLOT_SIZE,
                    height: ICON_SLOT_SIZE,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    opacity: 0.5,
                    cursor: 'pointer',
                    transform: expanded ? 'rotate(90deg)' : 'none',
                    transition: 'opacity 0.15s ease, transform 0.15s ease',
                    zIndex: 1,

                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: -4,
                    },

                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                >
                  <Icon
                    category="mui"
                    icon="ArrowRight"
                    size={ICON_SLOT_SIZE}
                  />
                </Box>
              ) : variant === 'group' ? (
                <Box
                  sx={{
                    width: ICON_SLOT_SIZE,
                    height: ICON_SLOT_SIZE,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    position: 'relative',
                    overflow: 'visible',
                  }}
                >
                  {groupLeadingSlot ??
                    (groupIcon ? (
                      <Box sx={{ opacity: 0.4, display: 'flex' }}>
                        <Icon
                          category="elementCategory"
                          icon={groupIcon}
                          size={ICON_SLOT_SIZE}
                        />
                      </Box>
                    ) : (
                      <Typography noWrap sx={SBEF_PREFIX_SX}>
                        {groupNumber != null ? `${groupNumber}.` : ''}
                      </Typography>
                    ))}
                </Box>
              ) : expandable && rowHovered && depth === 1 ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: ICON_SLOT_SIZE,
                    height: ICON_SLOT_SIZE,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpandedChange?.(!expanded);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      opacity: 0.5,
                      cursor: 'pointer',
                      transform: expanded ? 'rotate(90deg)' : 'none',
                      transition: 'opacity 0.15s ease, transform 0.15s ease',

                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: -4,
                      },

                      '&:hover': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Icon
                      category="mui"
                      icon="ArrowRight"
                      size={ICON_SLOT_SIZE}
                    />
                  </Box>
                </Box>
              ) : hideMappingColumns ? (
                <Box
                  sx={{
                    width: ICON_SLOT_SIZE,
                    height: ICON_SLOT_SIZE,
                    flexShrink: 0,
                  }}
                />
              ) : omitMainTypeIcon ? null : (
                <Box
                  sx={{
                    position: 'relative',
                    width: ICON_SLOT_SIZE,
                    height: ICON_SLOT_SIZE,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    opacity: dimContent ? 0.2 : 0.4,
                  }}
                >
                  {iconSlot ?? (
                    <Icon
                      category="elementCategory"
                      icon={categoryIcon}
                      size={ICON_SLOT_SIZE}
                    />
                  )}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                width: 2,
                flexShrink: 0,
              }}
            />

            {nameLeadingSlot}

            {/* Name + focus arrow: with quantity column, name is content-width in view mode; with hideQuantity, name fills the row. */}
            {/* paddingRight reserves space for the absolute-positioned kebab button so it doesn't intercept pointer events on the name spacer */}
            <Box
              onClick={
                onNameClick
                  ? (e) => {
                      e.stopPropagation();
                      onNameClick(e);
                    }
                  : undefined
              }
              sx={mergeSx(
                nameRowBaseSx,
                contentDimSx,
                shouldRenderMenuAction && hideQuantity
                  ? {
                      pr:
                        kebabLeadingSlot != null
                          ? kebabLeadingNamePaddingRight
                          : hideQuantityMenuNamePaddingRight,
                    }
                  : undefined,
                onNameClick ? pointerCursorSx : undefined,
              )}
            >
              {nameOverrideSlot ?? (
                <ValueField
                  value={nameValue}
                  isEditing={isEditingName}
                  onSave={handleNameSave}
                  onStartEditing={
                    nameReadonly ? () => undefined : startEditingName
                  }
                  onStopEditing={stopEditingName}
                  showHover={false}
                  textAlign="left"
                  fullWidth
                  contentSized={nameTrailingSlot != null}
                  triggerOn="doubleClick"
                  onDoubleClickOverride={onFocusClick ?? undefined}
                  displayStyle={
                    variant === 'group'
                      ? {
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '1.3px',
                          color: 'rgba(0,0,0,0.5)',
                          WebkitTextFillColor: 'rgba(0,0,0,0.5)',
                          ...nameDisplayStyle,
                        }
                      : {
                          color: 'rgba(0,0,0,0.8)',
                          WebkitTextFillColor: 'rgba(0,0,0,0.8)',
                          ...nameDisplayStyle,
                        }
                  }
                  editingStyle={
                    variant === 'group'
                      ? {
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '1.3px',
                          color: 'rgba(0,0,0,0.5)',
                          WebkitTextFillColor: 'rgba(0,0,0,0.5)',
                          ...nameDisplayStyle,
                        }
                      : {
                          color: 'rgba(0,0,0,0.8)',
                          WebkitTextFillColor: 'rgba(0,0,0,0.8)',
                          ...nameDisplayStyle,
                        }
                  }
                  placeholder={namePlaceholder}
                />
              )}
              {nameTrailingContent}
              {!isEditingName && (rowHovered || selected) && onFocusClick && (
                <Tooltip title="Open" placement="top" disableInteractive>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      onFocusClick();
                    }}
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      color: 'rgba(0,0,0,0.4)',
                      '&:hover': { color: 'rgba(0,0,0,0.8)' },
                      px: '2px',
                    }}
                  >
                    <Icon category="mui" icon="ArrowForward" size={14} />
                  </Box>
                </Tooltip>
              )}
            </Box>

            {!hideMappingColumns && !hideQuantity && (
              <Tooltip title={quantityTooltip}>
                <Box sx={mergeSx(mappingColumnsSx, contentDimSx)}>
                  {/* QuantityValue — inline editable with Figma ValueContainer states */}
                  <NumberValueField
                    displayStyle={{
                      ...(quantityColor && { color: quantityColor }),
                      ...(quantityFontWeight != null && {
                        fontWeight: quantityFontWeight,
                      }),
                      ...(quantityFontSize != null && {
                        fontSize: quantityFontSize,
                      }),
                    }}
                    value={qtyValue}
                    displayValue={
                      isEditingQty ? undefined : quantityDisplayValue
                    }
                    animatedValue={quantityDisplayNumber}
                    animationKey={quantityDisplayUnit}
                    isEditing={isEditingQty}
                    onSave={handleQtySave}
                    onStartEditing={
                      quantityReadonly ? () => undefined : startEditingQty
                    }
                    onStopEditing={stopEditingQty}
                    placeholder={quantityPlaceholder}
                    fillValue={quantityFillValue}
                    onFxClick={onQtyFxClick}
                    showHover={!quantityReadonly}
                  />

                  {/* Unit — 48px */}
                  <Box
                    sx={{
                      width: 48,
                      flexShrink: 0,
                      alignSelf: 'stretch',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <UnitSelector
                      unit={isEditingQty ? unit : (quantityDisplayUnit ?? unit)}
                      onClick={onUnitClick}
                      fontSize={quantityFontSize}
                      color={quantityColor}
                    />
                  </Box>
                </Box>
              </Tooltip>
            )}

            {innerTrailingSlot}

            {/* KPI + kebab: shared row, flush to the right (`right: 4`), no gap between KPI and kebab. */}
            {shouldRenderMenuAction && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 4,
                  top: '50%',
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0,
                  transform: 'translateY(-50%)',
                }}
              >
                {kpiSlotContent != null && (
                  <Box
                    sx={{
                      display: 'flex',
                      pointerEvents: kebabLeadingInteractive ? 'auto' : 'none',
                      ...(kpiClusterTranslateX !== 0 && {
                        transform: `translateX(${kpiClusterTranslateX}px)`,
                      }),
                    }}
                  >
                    {kpiSlotContent}
                  </Box>
                )}
                {(Boolean(onKebabClick) || showDisabledIcon) && (
                  <Tooltip
                    title={kebabTooltip ?? ''}
                    disableInteractive
                    placement="top"
                  >
                    <Box
                      component={onKebabClick ? 'button' : 'div'}
                      // An icon-only trigger, named after the row it belongs to
                      aria-label={`${nameValue || namePlaceholder} options`}
                      onClick={
                        onKebabClick
                          ? (e: React.MouseEvent<HTMLElement>) => {
                              e.stopPropagation();
                              onKebabClick(e);
                            }
                          : undefined
                      }
                      className={classes.kebab}
                    >
                      <Icon
                        category="mui"
                        icon={showDisabledIcon ? 'VisibilityOff' : kebabIcon}
                        size={
                          !showDisabledIcon && kebabIcon === 'MoreVert'
                            ? 18
                            : 12
                        }
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Drag handle — grab right edge to resize (element list only) */}
            {resizable && (
              <Box
                className={ELEMENT_LIST_RESIZE_HANDLE_CLASS}
                onMouseDown={handleDragStart}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  cursor: 'ew-resize',
                }}
              />
            )}
          </Box>

          {resizable && trailingSlot != null && (
            <Box
              sx={{
                alignItems: 'center',
                alignSelf: 'stretch',
                display: 'flex',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                pl: `${trailingSlotPaddingLeft}px`,
              }}
            >
              {trailingSlot}
            </Box>
          )}

          {/* depth-1 chevrons before HorizontalBars */}
          {resizable && trailingSlot == null && depth > 1 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'stretch',
                flexShrink: 0,
                gap: `${ICON_GAP}px`,
                px: '4px',
              }}
            >
              {Array.from({ length: depth - 1 }).map((_, i) => (
                <IndentChevron key={i} />
              ))}
            </Box>
          )}

          {/* HorizontalBars — fills remaining, proposals stacked without gaps */}
          {resizable && trailingSlot == null && (
            <Box
              sx={mergeSx(barsColumnSx, dimContent ? dimOpacitySx : undefined)}
            >
              {proposals.map((p, i) => (
                <HorizontalResultBar
                  key={i}
                  co2Value={p.co2Value}
                  costValue={p.costValue}
                  baselineCo2Value={p.baselineCo2Value}
                  color={p.color ?? DEFAULT_BAR_COLOR}
                  maxCo2={maxCo2}
                  maxCost={maxCost}
                  co2Segments={p.co2Segments}
                  hideCo2e={!co2eVisible.isEnabled}
                  hideCost={displayCurrency.isDisabled}
                  barHeight={
                    variant === 'group'
                      ? proposals.length === 1
                        ? GROUP_RESULT_BAR_HEIGHT
                        : 12
                      : 8
                  }
                />
              ))}
            </Box>
          )}

          {resizable && trailingSlot == null && (
            <ElementListResultColumns
              proposals={proposals}
              visible={columnsHovered || rowHovered || selected}
              dimmed={dimContent}
            />
          )}
        </Box>
      </>
    );
  },
);

export default ElementListRow;

const useStyles = makeStyles<{
  selected: boolean;
  resizable: boolean;
  hideSurfaceBackground: boolean;
  kebabVisible: boolean;
  dimContent: boolean;
  kebabClickable: boolean;
}>()(
  (
    { palette },
    {
      selected,
      resizable,
      hideSurfaceBackground,
      kebabVisible,
      dimContent,
      kebabClickable,
    },
  ) => ({
    resizableContainer: {
      backgroundColor: selected
        ? 'var(--element-list-selected-background, #E2F2FF)'
        : hideSurfaceBackground
          ? 'transparent'
          : palette.warmSurface.main,
      boxShadow: resizable
        ? 'var(--element-list-column-divider-shadow, inset -1px 0 0 rgba(0,0,0,0.1))'
        : 'none',
    },
    resizableContainerDivider: {
      backgroundColor: palette.warmSurface.main,
      boxShadow: resizable
        ? 'var(--element-list-column-divider-shadow, inset -1px 0 0 rgba(0,0,0,0.1))'
        : 'none',
    },
    kebab: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 20,
      height: 20,
      boxSizing: 'border-box',
      padding: 0,
      border: 'none',
      color: 'rgba(0,0,0,0.6)',
      cursor: kebabClickable ? 'pointer' : 'default',
      backgroundColor: selected
        ? 'var(--element-list-selected-background, #E2F2FF)'
        : hideSurfaceBackground
          ? 'transparent'
          : palette.warmSurface.main,
      // Hidden (opacity 0) until hovered / menu-open / persistent disabled icon,
      // then halved again on disabled rows so it dims with the rest of the row.
      // Hover grey comes from the row ::after wash (not a second inset tint).
      opacity: (kebabVisible ? 1 : 0) * (dimContent ? 0.5 : 1),
      transition: 'opacity ease-out 100ms',
    },
  }),
);
